import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import AuthenticatedImage from './AuthenticatedImage';
import HeartAnimation from './HeartAnimation';
import { Dress } from '../types';
import dressLikesService from '../services/dressLikes';
import { useAuth } from '../contexts/AuthContext';

interface DressCardProps {
  dress: Dress;
  isLiked: boolean;
  onLikeChange?: (dressId: string, isLiked: boolean) => void;
  showDesigner?: boolean;
  clickable?: boolean;
}

const DressCard: React.FC<DressCardProps> = ({ 
  dress, 
  isLiked: initialIsLiked,
  onLikeChange,
  showDesigner = true,
  clickable = true
}) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastTapRef = useRef<number>(0);
  const lastTouchEndRef = useRef<number>(0);

  // Handle double tap/click on the image.
  // Only a detected double-tap consumes the event (to like); a single tap is
  // left to propagate so the wrapping <Link> navigates to the dress detail page.
  const handleDoubleTap = async (e: React.MouseEvent | React.TouchEvent) => {
    const currentTime = Date.now();

    // On touch devices a tap fires onTouchEnd immediately followed by a synthetic
    // onClick. Ignore that trailing click so a single tap isn't misread as a double tap.
    if (e.type === 'click' && currentTime - lastTouchEndRef.current < 500) {
      return;
    }
    if (e.type === 'touchend') {
      lastTouchEndRef.current = currentTime;
    }

    const tapDelay = currentTime - lastTapRef.current;

    if (tapDelay < 300 && tapDelay > 0) {
      // Double tap detected — like the dress and prevent navigation
      e.preventDefault();
      await handleLike();
    }

    lastTapRef.current = currentTime;
  };

  const handleLike = async () => {
    if (isProcessing || !user) return;
    
    setIsProcessing(true);
    const newLikedState = !isLiked;
    
    // Optimistic update
    setIsLiked(newLikedState);
    
    if (newLikedState) {
      setShowHeartAnimation(true);
    }
    
    try {
      if (newLikedState) {
        await dressLikesService.likeDress(dress.id);
      } else {
        await dressLikesService.unlikeDress(dress.id);
      }
      
      // Notify parent component
      if (onLikeChange) {
        onLikeChange(dress.id, newLikedState);
      }
    } catch (error: any) {
      console.error('Failed to update like status:', error);
      
      // Handle access denied error
      if (error.response?.status === 403 && error.response?.data?.code === 'BIZ_002') {
        // Show user-friendly message
        alert('This dress requires exclusive access. Please contact us for more information.');
        
        // Notify parent to remove this dress from view
        if (onLikeChange) {
          onLikeChange(dress.id, false);
        }
      }
      
      // Revert optimistic update
      setIsLiked(!newLikedState);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHeartClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await handleLike();
  };

  const cardContent = (
    <>
      <div 
        className="relative aspect-[3/5] bg-gray-100"
        onClick={handleDoubleTap}
        onTouchEnd={handleDoubleTap}
      >
        {dress.thumbnailUrl ? (
          <AuthenticatedImage
            src={dress.thumbnailUrl}
            alt={dress.name}
            className="object-cover w-full h-full"
          />
        ) : (
          <div className="flex items-center justify-center text-gray-400">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Heart Icon - only show for authenticated users */}
        {user && (
          <button
            onClick={handleHeartClick}
            className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
            aria-label={isLiked ? "Unlike dress" : "Like dress"}
            disabled={isProcessing}
          >
            {isLiked ? (
              <HeartIconSolid className="w-5 h-5 text-red-500" />
            ) : (
              <HeartIcon className="w-5 h-5 text-gray-700" />
            )}
          </button>
        )}
        
        {/* Heart Animation */}
        {showHeartAnimation && (
          <HeartAnimation onComplete={() => setShowHeartAnimation(false)} />
        )}
      </div>
      
      <div className="p-4">
        {showDesigner && (
          <p className="text-sm font-bold text-black uppercase tracking-wide">
            {dress.designer.name}
          </p>
        )}
      </div>
    </>
  );

  if (clickable) {
    return (
      <Link
        to={`/home/dress/${dress.id}`}
        className="block bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div
      className="bg-white rounded-lg shadow-sm overflow-hidden"
    >
      {cardContent}
    </div>
  );
};

export default DressCard;